if (!customElements.get('wls-lwr-newsletter')) {
  class WLSLwrNewsletter extends HTMLElement {
    constructor() {
      super();

      this.endpoint = 'https://a.klaviyo.com/client/subscriptions/';
      this.apiRevision = '2024-10-15';
      // Source the email team segments article signups by in Klaviyo
      this.signupSource = 'article-promo-module';
      this.emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      this.onSubmit = this._onSubmit.bind(this);
      this.onInput = this._onInput.bind(this);
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this.form = this.querySelector('.js-wls-lwr-newsletter-form');
      this.input = this.querySelector('.js-wls-lwr-newsletter-input');
      this.submitBtn = this.querySelector('.js-wls-lwr-newsletter-submit button');
      this.errorEl = this.querySelector('.js-wls-lwr-newsletter-error');
      this.successEl = this.querySelector('.js-wls-lwr-newsletter-success');

      if (!this.form || !this.input) return;

      this.form.addEventListener('submit', this.onSubmit);
      this.input.addEventListener('input', this.onInput);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.input?.removeEventListener('input', this.onInput);
    }

    /**
     * Validate the email and subscribe it to the Klaviyo list
     * @param {SubmitEvent} event - The submit event
     * @returns {Promise<void>}
     */
    async _onSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;

      const email = this.input.value.trim();
      if (!this.emailPattern.test(email)) {
        this.showError(this.dataset.errorInvalid);
        this.input.focus();
        return;
      }

      this.clearError();
      this.setSubmitting(true);

      try {
        await this.subscribe(email);
        this.showSuccess();
        this.trackSubscribe();
        this.postSignupMessage();
      } catch (error) {
        this.showError(this.dataset.errorGeneric);
      } finally {
        this.setSubmitting(false);
      }
    }

    /**
     * Clear the error once the visitor edits the email
     * @returns {void}
     */
    _onInput() {
      if (this.input.getAttribute('aria-invalid') === 'true') this.clearError();
    }

    /**
     * POST the subscription to the Klaviyo Client API (responds 202 with no body)
     * @param {string} email - The email to subscribe
     * @returns {Promise<void>}
     */
    async subscribe(email) {
      const url = `${this.endpoint}?company_id=${encodeURIComponent(this.dataset.companyId)}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: this.apiRevision,
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: this.signupSource,
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    // $source is reserved and may not be set on existing profiles, signup_source is the backup
                    properties: {
                      $source: this.signupSource,
                      signup_source: this.signupSource,
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: { type: 'list', id: this.dataset.listId },
              },
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Klaviyo subscription failed: ${response.status}`);
      }
    }

    /**
     * Push the GA4 newsletter_subscribe event (same event as the Klaviyo popup and footer)
     * @returns {void}
     */
    trackSubscribe() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'newsletter_subscribe',
        form_source: 'klaviyo',
        form_location: this.dataset.formLocation,
        // No Klaviyo form here (API submit); reset so GTM doesn't reuse a form_id from an earlier push
        form_id: undefined,
      });
    }

    /**
     * Post the article signup message for GA4 tracking. The module renders on the page (no iframe),
     * so it posts to its own window, targeting the current origin (the live domain on the live site)
     * @returns {void}
     */
    postSignupMessage() {
      window.postMessage({ event: 'article_signup_success', form_location: 'article_promo' }, window.location.origin);
    }

    /**
     * Toggle the submitting state of the button
     * @param {boolean} submitting - Whether the form is submitting
     * @returns {void}
     */
    setSubmitting(submitting) {
      this.isSubmitting = submitting;
      if (!this.submitBtn) return;

      this.submitBtn.setAttribute('aria-busy', submitting ? 'true' : 'false');
    }

    /**
     * Show an error message (announced through the role="alert" element)
     * @param {string} message - The message to show
     * @returns {void}
     */
    showError(message) {
      this.input.setAttribute('aria-invalid', 'true');
      if (this.errorEl) this.errorEl.textContent = message;
    }

    /**
     * Clear the error message
     * @returns {void}
     */
    clearError() {
      this.input.removeAttribute('aria-invalid');
      if (this.errorEl) this.errorEl.textContent = '';
    }

    /**
     * Replace the form with the success message (announced through the role="status" element)
     * @returns {void}
     */
    showSuccess() {
      if (!this.successEl) return;

      this.successEl.textContent = this.successEl.dataset.message;
      this.form.hidden = true;
      this.successEl.focus();
    }
  }

  customElements.define('wls-lwr-newsletter', WLSLwrNewsletter);
}
